import type {
  WeibullRow,
  WeibullWorkOrders,
  AssetWeibullMaintenanceStatus,
} from './types';

export const getUniqueMakeModels = (
  data: WeibullRow[],
  selectedComponent: string,
  selectedSubComponent: string
) => {
  const uniqueMap = data
    .filter(
      (item) =>
        item.components_category === selectedComponent &&
        item.sub_component_category === selectedSubComponent
    )
    .reduce((acc, item) => {
      const key = `${item.make}-${item.model}`;
      if (!acc.has(key)) {
        acc.set(key, { make: item.make, model: item.model });
      }
      return acc;
    }, new Map());

  return Array.from(uniqueMap.values());
};

export const getUniqueComponents = (data: WeibullRow[]) => {
  const uniqueComponents = new Set<string>();

  data.forEach((item) => {
    uniqueComponents.add(item.components_category);
  });

  return Array.from(uniqueComponents);
};

export const getUniqueSubComponents = (
  data: WeibullRow[],
  selectedComponent: string
) => {
  const uniqueSubComponents = new Set<string>();

  data.forEach((item) => {
    if (item.components_category === selectedComponent) {
      uniqueSubComponents.add(item.sub_component_category);
    }
  });

  return Array.from(uniqueSubComponents);
};

export function extractWeibullParams(rows: WeibullRow[]) {
  const repair = rows.find((r) => r.actions_performed === 'REPAIR');
  const replace = rows.find((r) => r.actions_performed === 'REPLACE');

  return {
    weibullKRepair: repair?.weibull_k ?? 0,
    weibullLambdaRepair: repair?.weibull_lambda ?? 0,
    weibullKReplace: replace?.weibull_k ?? 0,
    weibullLambdaReplace: replace?.weibull_lambda ?? 0,
  };
}

/**
 *
 * @param lambda weibullLambdaRepair
 * @param k  weibullKRepair
 * @param lambda2  weibullLambdaReplace
 * @param k2 weibullKReplace
 * @param points
 * @returns array of points to plot on chart
 */
export function generateWeibullData(
  lambda: number,
  k: number,
  lambda2: number,
  k2: number,
  points = 200
) {
  const data = [];
  const maxTime = 365;

  for (let i = 0; i < points; i++) {
    const t = (i / (points - 1)) * maxTime;

    const cdfRepair = 1 - Math.exp(-Math.pow(t / lambda, k));
    const cdfReplace = 1 - Math.exp(-Math.pow(t / lambda2, k2));

    const reliabilityRepair = (1 - cdfRepair) * 100;
    const reliabilityReplace = (1 - cdfReplace) * 100;

    data.push({
      time: t,
      reliabilityRepair,
      reliabilityReplace,
    });
  }

  return data;
}

export function predictMaintenanceNeeds(
  workOrders: WeibullWorkOrders[],
  eolData: WeibullRow[]
) {
  const result = { repair: 0, replace: 0 };
  const today = new Date();
  const thirtyDaysFromNow = new Date(today);
  thirtyDaysFromNow.setDate(today.getDate() + 30);

  workOrders.forEach((order) => {
    const orderDate = new Date(order.date_of_orders);
    const action = order.actions_performed.toUpperCase();

    const eolMatch = eolData.find((eol) => eol.actions_performed === action);
    if (!eolMatch?.eol) return;
    const nextMaintenanceDate = new Date(orderDate);
    nextMaintenanceDate.setDate(
      nextMaintenanceDate.getDate() + Math.round(eolMatch.eol)
    );
    const isOverdue = nextMaintenanceDate < today;
    const isUpcoming = nextMaintenanceDate <= thirtyDaysFromNow;

    if (isOverdue || isUpcoming) {
      if (action === 'REPAIR') result.repair++;
      else if (action === 'REPLACE') result.replace++;
    }
  });

  return result;
}

export function getAssetsTable(
  weibullRows: WeibullRow[],
  workOrders: WeibullWorkOrders[]
): AssetWeibullMaintenanceStatus[] {
  const currentDate = new Date();
  const assetIds = [...new Set(workOrders.map((wo) => wo.asset_id))];

  const repairEOL =
    weibullRows.find((wr) => wr.actions_performed === 'REPAIR')?.eol ?? 0;
  const replaceEOL =
    weibullRows.find((wr) => wr.actions_performed === 'REPLACE')?.eol ?? 0;

  return assetIds.map((assetId) => {
    const assetWorkOrders = workOrders.filter((wo) => wo.asset_id === assetId);

    const repairOrders = assetWorkOrders.filter(
      (wo) => wo.actions_performed === 'REPAIR'
    );
    const lastRepairDate =
      repairOrders.length > 0
        ? new Date(
            Math.max(
              ...repairOrders.map((wo) => new Date(wo.date_of_orders).getTime())
            )
          )
        : null;

    const replaceOrders = assetWorkOrders.filter(
      (wo) => wo.actions_performed === 'REPLACE'
    );
    const lastReplaceDate =
      replaceOrders.length > 0
        ? new Date(
            Math.max(
              ...replaceOrders.map((wo) =>
                new Date(wo.date_of_orders).getTime()
              )
            )
          )
        : null;

    const daysSinceLastRepair = lastRepairDate
      ? Math.floor(
          (currentDate.getTime() - lastRepairDate.getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : null;

    const daysSinceLastReplace = lastReplaceDate
      ? Math.floor(
          (currentDate.getTime() - lastReplaceDate.getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : null;

    const daysUntilNextRepair =
      daysSinceLastRepair !== null && repairEOL > 0
        ? Number((repairEOL - daysSinceLastRepair).toFixed(2))
        : 0;

    const daysUntilNextReplace =
      daysSinceLastReplace !== null && replaceEOL > 0
        ? Number((replaceEOL - daysSinceLastReplace).toFixed(2))
        : 0;

    return {
      asset_id: assetId,
      daysSinceLastRepair,
      daysSinceLastReplace,
      daysUntilNextRepair,
      daysUntilNextReplace,
    };
  });
}

export function exportWeibullCSV(
  data: AssetWeibullMaintenanceStatus[],
  makeModel: string
) {
  if (!data || data.length === 0) return;

  const title = `Survivability Weibull ${makeModel} Asset Details`;

  const keys = [
    'asset_id',
    'daysSinceLastRepair',
    'daysSinceLastReplace',
    'daysUntilNextRepair',
    'daysUntilNextReplace',
  ];

  const formatHeader = (key: string) => {
    const noUnderscore = key.replace(/_/g, ' ');
    const spaced = noUnderscore.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.toUpperCase();
  };

  const headers = keys.map(formatHeader);

  const rows = data.map((row) => {
    return [
      row.asset_id ?? '',
      row.daysSinceLastRepair ?? '',
      row.daysSinceLastReplace ?? '',
      row.daysUntilNextRepair ?? '',
      row.daysUntilNextReplace ?? '',
    ].join(',');
  });

  const csvString = [title, headers.join(','), ...rows].join('\n');

  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;

  const safeName = makeModel.replace(/[^a-z0-9]/gi, '_');
  link.download = `Weibull_${safeName}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
